import BaseModal from 'components/BaseModal'
import WizardFormV2 from 'components/WizardFormV2'
import { MODAL_SIZE_LARGE, SEVERITY_COLOR } from 'constants/Layout'
import { Switch, Tooltip, Typography } from 'antd'
import { FieldTypes, nameRegexValidator } from 'utils/formUtils';
import { CustomToolAPI } from "restAPI/ToolsIntegrationAPI";
import React, { forwardRef, useCallback, useRef, useState, useMemo } from 'react'
import _ from 'lodash';
import message from 'components/CustomMessage';
import { ToolCategory } from './ToolCategory';
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import { HelpIcon } from 'components/Icons';
import parseHtml from 'utils/parserUtil';
import { useAutoFocus } from 'hooks/useFocusHelpers';
import useEnabledFindingStatus from 'hooks/useEnabledFindingStatus';
import { map, toLower } from 'utils/lodashUtils';

export const CUSTOM_PREFIX = 'Custom-'
export const ToolMapField = {
    'SUMMARY': 'Summary',
    "SEVERITY": <Vocabulary name='severity' />,
    "STATUS": "Status",
    "TOOl_CATEGORY": <>Tool Finding <Vocabulary name="category" /></>,
    "MITIGATION": "Mitigation",
    'DESCRIPTION': 'Description',
    "IMPACT": "Impact",
    "CWE": 'CWE',
    "CVE": 'CVE',
    "UNIQUE_ID_FROM_TOOL": "Tool Finding ID",
    "SCAN_DATE": "Scan Date",
    "DEVICE": 'Device',
    "COMPONENT_NAME": "Component Name",
    "COMPONENT_VERSION": "Component Version",
    "URL": "Tool Finding URL",
    "URL_ENDPOINT": "URL/Endpoint",
    "TAGS": "Tags",
    "REPOSITORY": "Repository",
    "DEVELOPER": "Developer",
    "IMAGE_NAME": "Image Name",
    "IMAGE_TAG": "Image Tag",
    "VENDOR": "Vendor",
    "HOST": "Host Name",
    "CVSS_VECTOR": "CVSS Vector",
    "CVSS_SCORE": "Base Score",
    "STEPS_TO_REPRODUCE": "Steps to_Reproduce",
    "FILE_NAME": "File Name",
    "FILE_PATH": "File Path",
    "TICKET_ID": "Ticket ID",
    "REOPEN_DATE": <>Re-<Vocabulary name = "open"/></>,
}
export default forwardRef(function CustomToolForm({ afterClose = _.noop, editId, ...props }, ref) {
    const { toolData, fieldCheck } = props;
    const thisRef = useRef();
    const sourceInutFieldRef = useAutoFocus();
    const [deDupField, setDedupField] = useState(fieldCheck ? { ...fieldCheck } : { SUMMARY: true });
    const enabledStatuses = map(useEnabledFindingStatus(), status => {
        const statusMap = {
            'OPEN': 'open',
            'CONFIRMED': 'confirm',           
            'ACCEPTRISK': 'acceptRisk',       
            'FALSEPOSITIVE': 'falsePositive', 
            'MITIGATED': 'mitigated',
            'SUPPRESSED': 'suppressed',
            'IN_PROGRESS': 'inProgress',      
            'TRIAGE': 'triage',
            'CONTROLLED': 'controlled'
        };
        return statusMap[status] || toLower(status).replace(/_/g, '');
    });

    const initValue = !toolData
        ?
        {
            'SUMMARY': 'Summary',
            "SEVERITY": "Severity",
            "STATUS": "Status",
            "TOOl_CATEGORY": "",
            "MITIGATION": "Mitigation",
            'DESCRIPTION': 'Description',
            "IMPACT": "Impact",
            "CWE": 'CWE',
            "CVE": 'CVE',
            'DEVICE': 'Device',
            "COMPONENT_NAME": "ComponentName",
            "COMPONENT_VERSION": "ComponentVersion",
            "URL": "URL",
            "URL_ENDPOINT": "URL/Endpoint",
            "TAGS": "Tags",
            "REPOSITORY": "Repository",
            "DEVELOPER": "Developer",
            "IMAGE_NAME": "ImageName",
            "IMAGE_TAG": "ImageTag",
            "VENDOR": "Vendor",
            "CVSS_VECTOR": "CVSS Vector",
            "CVSS_SCORE": "CVSSScore",
            "STEPS_TO_REPRODUCE" : "StepsToReproduce",
            "FILE_NAME": "FileName",
            "FILE_PATH": "FilePath",
            "TICKET_ID": "TicketId",
            "REOPEN_DATE": "Re-OpenDate",
        }
        :
        { ...toolData }


    const getBasicInfoFields = [{
        name: "name",
        label: "Source",
        colSpan: 12,
        type: FieldTypes.ANTD_DEFAULT_INPUT,
        required: true,
        rules: [nameRegexValidator({ min: 3 }),
        {
            validator: (rule, value) => {
                const customTools = _.map(props.toolsData, (tool) => tool.name )
                if(value && customTools.includes(CUSTOM_PREFIX.concat(value))){
                    return Promise.reject("Name already exists")
                }
                return Promise.resolve()
            }
        }
    ],
        disabled: !_.isUndefined(editId),
        props: {
            ref: sourceInutFieldRef,
            prefix: CUSTOM_PREFIX
        }
    }, {
        name: "category",
        label: "Scan Type",
        type: FieldTypes.MULTI_SELECT,
        placeholder: "add scan type e.g. SAST, DAST etc.",
        options: _.map(_.filter(Object.keys(ToolCategory), category => (["AUTOMATION", "VERSION_CONTROL", "DYNAMIC"]).indexOf(category) === -1), category => ({ value: category, label: ToolCategory[category] })),
        required: true,
        colSpan: 24
    }]

    const isChecked = useCallback(fieldId => {
        if (!_.isUndefined(deDupField[fieldId])) {
            return deDupField[fieldId]
        }
        return false;
    }, [deDupField])

    const handleSwitch = useCallback((isChecked, fieldId) => {
        const checkList = { ...deDupField };
        checkList[fieldId] = isChecked;
        setDedupField(checkList)
    }, [deDupField]);

    const AddonField = useCallback((fieldId) => (
        <Switch size="small" checked={isChecked(fieldId)} onChange={isChecked=> handleSwitch(isChecked, fieldId)} key={fieldId} id={fieldId} />
    ), [handleSwitch, isChecked]);

    const InstructionsDetail = () => (
        <div className="m-b-m">
            <Typography.Text>Set the Title for your Custom Tool as you wish to display it at ArmorCode platform e.g. You can name "Mitigation" as "Solution" or "Fix" in your tool. You can also turn the <Typography.Text strong>Toggle button</Typography.Text> on or off to mark that field for Deduplication.</Typography.Text>
        </div>
    )

    const commonSeverityProps = useMemo(() =>({
        colSpan: 24,
        labelCol: { span: 24 },
        wrapperCol: { span: 24 },
        style: {flex: "0 0 20%"},
    }),[])


    const statusSeverityProps = useMemo(() =>({
        colSpan: 8,
        labelCol: { span: 24 },
        wrapperCol: { span: 24 },
        style: {flex: "33% 33% 33%"}
    }), []);

    const getStatusMappingFields = useCallback((enabledStatuses, statusSeverityProps) => {
        return enabledStatuses.map(status => {
            const statusVocabularyMap = {
                'open': 'open',
                'confirm': 'confirmed',
                'acceptRisk': 'acceptRisk',
                'falsePositive': 'falsePositive',
                'mitigated': 'mitigated',
                'suppressed': 'suppressed',
                'inProgress': 'inProgressStatus',
                'triage': 'triageStatus',
                'controlled': 'controlledStatus'
            };
            
            const vocabularyKey = statusVocabularyMap[status] || status;
            
            return {
                name: status,
                label: getVocabulary(vocabularyKey),  
                ...statusSeverityProps
            };
        });
    }, []);

    const getMappingsFields = useMemo(() => [
        {
            name: "SUMMARY",
            label: ToolMapField.SUMMARY,
            required: true,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('SUMMARY'),
            },
            className: "custom-tool-label",
        },
        {
            name: "DESCRIPTION",
            label: ToolMapField.DESCRIPTION,
            required: true,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('DESCRIPTION')
            },
            className: "custom-tool-label",
        },
        {
            name: "MITIGATION",
            label: ToolMapField.MITIGATION,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('MITIGATION')
            },
            className: "custom-tool-label",
        },
        {
            name: "TOOl_CATEGORY",
            label: <>Tool Finding <Vocabulary name="category" /></>,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('TOOl_CATEGORY')
            },
            className: "custom-tool-label",
        },
        {
            name: "CWE",
            label: ToolMapField.CWE,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('CWE')
            },
            className: "custom-tool-label",
        },
        {
            name: "CVE",
            label: ToolMapField.CVE,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('CVE')
            },
            className: "custom-tool-label",
        },
        {
            name: "IMPACT",
            label: ToolMapField.IMPACT,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('IMPACT')
            },
            className: "custom-tool-label",
        },
        {
            name: "UNIQUE_ID_FROM_TOOL",
            label: ToolMapField.UNIQUE_ID_FROM_TOOL,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('UNIQUE_ID_FROM_TOOL')
            },
            className: "custom-tool-label",
        },
        {
            name: "SEVERITY",
            label: <Vocabulary name='severity' />,
            required: true,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('SEVERITY')
            },
            className: "custom-tool-label",
        },
        {
            name: "SCAN_DATE",
            label: "Scan Date",
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('SCAN_DATE')
            },
            className: "custom-tool-label",
        },
        {
            name: "DEVICE",
            label: ToolMapField.DEVICE,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('DEVICE')
            },
            className: "custom-tool-label",
        },
        {
            name: "COMPONENT_NAME",
            label: ToolMapField.COMPONENT_NAME,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('COMPONENT_NAME')
            },
            className: "custom-tool-label",
        },
        {
            name: "COMPONENT_VERSION",
            label: parseHtml(ToolMapField.COMPONENT_VERSION.split(' ').join('<br/>')),
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('COMPONENT_VERSION')
            },
            className: "custom-tool-label",
        },
        {
            name: "URL",
            label: ToolMapField.URL,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('URL')
            },
            className: "custom-tool-label",
        },
        {
            name: "TAGS",
            label: ToolMapField.TAGS,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('TAGS')
            },
            className: "custom-tool-label",
        },
        {
            name: "REPOSITORY",
            label: ToolMapField.REPOSITORY,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('REPOSITORY')
            },
            className: "custom-tool-label",
        },
        {
            name: "DEVELOPER",
            label: ToolMapField.DEVELOPER,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('DEVELOPER')
            },
            className: "custom-tool-label",
        },
        {
            name: "IMAGE_NAME",
            label: ToolMapField.IMAGE_NAME,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('IMAGE_NAME')
            },
            className: "custom-tool-label",
        },
        {
            name: "IMAGE_TAG",
            label: ToolMapField.IMAGE_TAG,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('IMAGE_TAG')
            },
            className: "custom-tool-label",
        },
        {
            name: "VENDOR",
            label: ToolMapField.VENDOR,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('VENDOR')
            },
            className: "custom-tool-label",
        },
        {
            name: "HOST",
            label: ToolMapField.HOST,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('HOST')
            },
            className: "custom-tool-label",
        },
        {
            name: "CVSS_VECTOR",
            label: ToolMapField.CVSS_VECTOR,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('CVSS_VECTOR')
            },
            className: "custom-tool-label",
        },
        {
            name: "CVSS_SCORE",
            label: ToolMapField.CVSS_SCORE,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('CVSS_SCORE')
            },
            className: "custom-tool-label",
        },
        {
            name: "STATUS",
            label: "Status",
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('STATUS')
            },
            className: "custom-tool-label",
        },
        {
            name: "STEPS_TO_REPRODUCE",
            label: parseHtml(ToolMapField.STEPS_TO_REPRODUCE.split('_').join('<br/>')),
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('STEPS_TO_REPRODUCE')
            },
            className: "custom-tool-label",
        },
        {
            name: "FILE_NAME",
            label: ToolMapField.FILE_NAME,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('FILE_NAME')
            },
            className: "custom-tool-label",
        },
        {
            name: "FILE_PATH",
            label: ToolMapField.FILE_PATH,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('FILE_PATH')
            },
            className: "custom-tool-label",
        },
        {
            name: "TICKET_ID",
            label: ToolMapField.TICKET_ID,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('TICKET_ID')
            },
            className: "custom-tool-label",
        },
        {
            name: "URL_ENDPOINT",
            label: ToolMapField.URL_ENDPOINT,
            colSpan: 11,
            labelCol: { span: 7 },
            wrapperCol: { span: 17 },
            props: {
                addonAfter: AddonField('URL_ENDPOINT')
            },
            className: "custom-tool-label",
        },
        {
            name: "REOPEN_DATE",
            label: ToolMapField.REOPEN_DATE,
            colSpan: 13,
            labelCol: { span: 10 },
            wrapperCol: { span: 14 },
            props: {
                addonAfter: AddonField('REOPEN_DATE')
            },
            className: "custom-tool-label",
        },
        {
            name: "dateFormat",
            label: "Date Format",
            wrapperCol: { span: 8 },
            help: <>We support various date formats, including MM-dd-yyyy. For a comprehensive list, please refer to <a href='https://docs.oracle.com/en/java/javase/17/docs/api/java.base/java/time/format/DateTimeFormatter.html' target='_blank' rel="noreferrer">this link</a>.</>,
        },
        {
            label: <><Vocabulary name='severity' /> Mapping Fields <Tooltip title="Use comma separated value for multiple mapping
            "> <HelpIcon /></Tooltip> </>,
            type: FieldTypes.GROUP,

            fields: [{
                name: "critical",
                label: <Vocabulary name="critical"/>,
                ...commonSeverityProps
            }, {
                name: "high",
                label: <Vocabulary name="high"/>,
                ...commonSeverityProps
            }, {
                name: "medium",
                label: <Vocabulary name="medium"/>,
                ...commonSeverityProps
            }, {
                name: "low",
                label: <Vocabulary name="low"/>,
                ...commonSeverityProps
            }, {
                name: "info",
                label: <Vocabulary name="info"/>,
                ...commonSeverityProps
            }]
        },
        {
            label: "Status Mapping Fields",
            type: FieldTypes.GROUP,
            style: { marginTop: 15 },
            fields: getStatusMappingFields(enabledStatuses, statusSeverityProps)
        },
        {
            render: (props) => (<div className='p-y-sm' {...props} />),
        },
    ], [AddonField,commonSeverityProps, statusSeverityProps,enabledStatuses,getStatusMappingFields]);

    const pages = [{
        name: "Instructions",
        fields: getBasicInfoFields,
    }, {
        name: "Details",
        fields: getMappingsFields,
        formLayout: "horizontal",
        component: InstructionsDetail
    }]
    const onOk = useCallback(async (values) => {
        const toolObj = modifyBeforeUpload(values, deDupField, enabledStatuses);
        if (_.isUndefined(editId)) {
            await CustomToolAPI.createCustomTool(toolObj).promise;
        } else {
            await CustomToolAPI.updateCustomTool(editId, toolObj).promise;
        }
    }, [deDupField, editId, enabledStatuses]);

    const onSuccess = useCallback(() => {
        message.success(`Successfully ${editId ? 'updated' : 'added new'} Security Tool`);
        thisRef.current.close();
    }, [thisRef, editId])

    const onCancel = useCallback(() => {
        thisRef.current.close();
    }, [thisRef])

    return (
        <BaseModal
            modalProps={{
                title: <>{!editId ? "Configure" : "Edit"} Custom Tool</>,
                footer: null,
                width: MODAL_SIZE_LARGE,
                className: "modal-full-height no-footer",
            }}
            ref={thisRef}
            afterClose={afterClose}
        >
            <WizardFormV2
                isEdit={!_.isUndefined(editId)}
                onCancel={onCancel}
                pages={pages}
                initialValues={initValue}
                api={onOk}
                onSuccess={onSuccess}
                validateOnBackwardStep={!_.isUndefined(editId)}
            />
        </BaseModal>
    )
});

function modifyBeforeUpload(data, deDupObj, enabledStatuses) {
    const toolData = {};
    toolData.fieldMapping = {};
    toolData.name = `${CUSTOM_PREFIX}${data.name}`;
    toolData.toolType = data.category;
    toolData.dateFormat = data.dateFormat;
    delete data.name;
    delete data.category;
    delete data.dateFormat;

    const otherProperties = {};
    Object.keys(SEVERITY_COLOR).forEach(s => {
        if(data[s]){
            otherProperties[s] = data[s];
        }
        delete data[s];
    });

    const statusOtherProperties = {};
    _.forEach(enabledStatuses, status => {
        if(data[status]){
            statusOtherProperties[status] = data[status];
        }
        delete data[status];
    });

    Object.keys(data).forEach(key => {
        if (!_.isEmpty(key)) {
            toolData.fieldMapping[key] = {}
            toolData.fieldMapping[key]['customFieldName'] = data[key];
            toolData.fieldMapping[key]['isDedupField'] = _.isUndefined(deDupObj[key]) ? false : deDupObj[key];
            
            if(key === "SEVERITY"){
                toolData.fieldMapping[key].otherProperties = otherProperties;
            }

            if(key === "STATUS"){
                toolData.fieldMapping[key].otherProperties = statusOtherProperties;
            }
        }
    });

    return toolData;
}
